import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button, message, Tag } from 'antd';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import {
	areasRestCrudThunksForBuyer,
	areasRestCrudThunksForSupplier,
	fetchAreasWithHeirarchy,
} from '../../thunks/areas_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { connect } from 'react-redux';
import OWSimpleHierarchyEdit from '../common/OWSimpleHierarchyEdit';
import AddHierarchyEntityPopup from '../common/AddHierarchyEntityPopup';

const normalize = (nodes = []) =>
	nodes.map((n) => ({
		...n,
		key: String(n.id),
		title: n.name,
		children: normalize(n.children || []),
	}));

const AreasDisplay: FC<any> = ({
	fetchAreas,
	location,
	deleteArea,
	userType,
	currentUser,
	companyConfig,
	createArea,
	updateArea,
}): React.ReactElement => {
	const [loading, setLoading] = useState(false);
	const [areas, setAreas] = useState([]);
	const [addAreaVisible, setAddAreaVisible] = useState(false);

	const locationId = useMemo(() => nullSafeGet('id', location), [location]);

	const refreshAreas = useCallback(() => {
		if (locationId) {
			setLoading(true);
			fetchAreas({
				locationId,
				...(location && location.locationTypeId && { locationTypeId: location.locationTypeId }),
			})
				.then((a) => setAreas(normalize(a)))
				.catch(message.error)
				.finally(() => setLoading(false));
		}
	}, [fetchAreas, locationId]);

	useEffect(() => {
		refreshAreas();
	}, []);

	const _showAreaModal = useCallback(() => setAddAreaVisible(true), []);
	const _hideAreaModal = useCallback(() => {
		setAddAreaVisible(false);
	}, []);

	const onAreaAddSuccess = useCallback(() => {
		_hideAreaModal();
		refreshAreas();
	}, [_hideAreaModal, refreshAreas]);

	const getChildrenCount = () => {
		return new Promise((resolve) => resolve(0));
	};

	const requiredParams = useMemo(() => ({ locationId, images: [] }), [locationId]);

	return (
		<Card
			title="Areas"
			extra={
				<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_AREAS}>
					<Button type="primary" onClick={_showAreaModal}>
						Add Area
					</Button>
				</AccessPermissionChecker>
			}
			loading={loading}
		>
			<OWSimpleHierarchyEdit
				sourceData={areas}
				allowedToEdit={isUserAllowedToAccess(userType)(
					PERMISSION_NAMES.MODIFY_AREAS,
					companyConfig,
					nullSafeGet('roles', currentUser)
				)}
				entityName={'area'}
				requiredParams={requiredParams}
				createEntity={createArea}
				updateEntity={updateArea}
				deleteEntity={deleteArea}
				getChildrenCount={getChildrenCount}
				refreshEntities={refreshAreas}
				renderTitle={(record) => (
					<div className="flex flex-row items-center gap-2" title={nullSafeGet('name', record)}>
						{nullSafeGet('name', record)}
						{record.locationId ? null : <Tag>Common</Tag>}
					</div>
				)}
				canEditTitle={(record) => !!record.locationId}
				canDelete={(record) => !!record.locationId}
			/>
			{addAreaVisible && (
				<AddHierarchyEntityPopup
					parentData={null}
					entityName={'area'}
					onSuccess={onAreaAddSuccess}
					onCancel={_hideAreaModal}
					onSave={createArea}
					requiredParams={requiredParams}
				/>
			)}
		</Card>
	);
};

const mapStateToProps = (state) => ({
	areas: state.areas,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchAreas: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			fetchAreasWithHeirarchy(ownProps.userType)(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	deleteArea: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? areasRestCrudThunksForSupplier.delete(entity, 'id', null, true)
				: areasRestCrudThunksForBuyer.delete(entity, 'id', null, true)
		),
	createArea: (entity, primaryKey, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? areasRestCrudThunksForSupplier.create(entity, primaryKey, targetCollectionName)
				: areasRestCrudThunksForBuyer.create(entity, primaryKey, targetCollectionName)
		),
	updateArea: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? areasRestCrudThunksForSupplier.update(entity)
				: areasRestCrudThunksForBuyer.update(entity)
		),
});

const ComponentWithoutUserType = connect(mapStateToProps, mapDispatchToProps)(AreasDisplay);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
